import { useEffect, useMemo, useRef, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../../components/Skeleton.js";
import StatusBadge from "../../components/StatusBadge.js";
import { Link } from "react-router-dom";
import { api, ApiError, ReplayJob } from "../../lib/api.js";
import Drawer from "../../components/Drawer.js";
import { useTableState } from "../../lib/useTableState.js";
import TableToolbar from "../../components/TableToolbar.js";
import { useLiveEvents } from "../../lib/useLiveEvents.js";

type TabKey = "all" | "queued" | "running" | "completed" | "failed" | "deadlettered" | "schedules";

type Scheduler = Awaited<ReturnType<typeof api.scheduledSync>>;

const JOB_TABS: { key: Exclude<TabKey, "schedules">; label: string; matcher: (j: ReplayJob) => boolean }[] = [
  { key: "all",          label: "All",            matcher: () => true },
  { key: "queued",       label: "Queued",         matcher: (j) => j.status === "queued" },
  { key: "running",      label: "Running",        matcher: (j) => j.status === "running" },
  { key: "completed",    label: "Completed",      matcher: (j) => j.status === "succeeded" || j.status === "completed" },
  { key: "failed",       label: "Failed",         matcher: (j) => j.status === "failed" },
  { key: "deadlettered", label: "Dead-letter",    matcher: (j) => j.status === "dead-lettered" }
];

function jobPill(status?: string) {
  return <StatusBadge status={status ?? "—"} />;
}

function fmt(date?: string): string {
  return date ? new Date(date).toLocaleString() : "—";
}

export default function AdminJobs() {
  const [jobs, setJobs] = useState<ReplayJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [workspaceFilter, setWorkspaceFilter] = useState<string>("all");
  const [selected, setSelected] = useState<ReplayJob | null>(null);
  const [scheduler, setScheduler] = useState<Scheduler | null>(null);

  // Status priority for sort: active work first, then waiting, then
  // recent failures, then recent successes. Inside each bucket the most
  // recently requested/updated row wins.
  const STATUS_RANK: Record<string, number> = {
    "running": 0,
    "active": 0,
    "queued": 1,
    "delayed": 2,
    "failed": 3,
    "dead-lettered": 4,
    "completed": 5,
    "succeeded": 5,
    "cancelled": 6
  };
  const sortJobs = (list: ReplayJob[]): ReplayJob[] =>
    [...list].sort((a, b) => {
      const ra = STATUS_RANK[a.status ?? ""] ?? 99;
      const rb = STATUS_RANK[b.status ?? ""] ?? 99;
      if (ra !== rb) return ra - rb;
      const at = (a.updatedAt ?? a.createdAt ?? (a as { requestedAt?: string }).requestedAt) ?? "";
      const bt = (b.updatedAt ?? b.createdAt ?? (b as { requestedAt?: string }).requestedAt) ?? "";
      return bt.localeCompare(at);
    });

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [{ jobs }, sched] = await Promise.all([
        api.jobs(),
        api.scheduledSync().catch(() => null)
      ]);
      setJobs(sortJobs(jobs ?? []));
      setScheduler(sched);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // Lower-cadence polling (15s) since live events keep status fresh
    // between polls; the poll is a safety net for events we may have
    // missed (eventbus reconnect, browser tab unfocus, etc.).
    const id = setInterval(() => { void refresh(); }, 15_000);
    return () => clearInterval(id);
  }, []);

  // Live updates — subscribe to the platform-wide job channel and merge
  // status / progress changes into local state immediately. Without
  // this, the user only sees changes after the next 15s poll.
  const live = useLiveEvents({
    scope: "platform",
    categories: ["job"],
    tailMax: 200
  });
  const lastEventIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (live.events.length === 0) return;
    const newest = live.events[0];
    if (newest.id === lastEventIdRef.current) return;
    lastEventIdRef.current = newest.id;
    // Pull a fresh snapshot — events tell us *something* changed; we
    // re-fetch the whole list to keep status + progress + result in
    // sync with the source of truth (cheap; the endpoint already
    // returns the full set).
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live.events[0]?.id]);

  const types = useMemo(() => Array.from(new Set(jobs.map((j) => j.type ?? "—"))).sort(), [jobs]);
  const workspaces = useMemo(
    () => Array.from(new Set(jobs.map((j) => (j as { workspaceId?: string }).workspaceId ?? "—"))).sort(),
    [jobs]
  );

  const filtered = useMemo(() => {
    if (activeTab === "schedules") return [];
    const tab = JOB_TABS.find((t) => t.key === activeTab);
    const matcher = tab?.matcher ?? (() => true);
    return jobs.filter((j) => {
      if (!matcher(j)) return false;
      if (typeFilter !== "all" && (j.type ?? "—") !== typeFilter) return false;
      const ws = (j as { workspaceId?: string }).workspaceId ?? "—";
      if (workspaceFilter !== "all" && ws !== workspaceFilter) return false;
      return true;
    });
  }, [jobs, activeTab, typeFilter, workspaceFilter]);

  const t = useTableState(filtered as unknown as Array<Record<string, unknown>>, {
    searchFields: ["id", "type", "status", "workspaceId", "requestedByUserId"],
    initialPageSize: 25
  });

  type ScheduledTargetRow = {
    workspaceId: string;
    environment: string;
    serviceName: string;
    type: string;
    lastEnqueuedAt?: string | null;
    lastStatus?: string | null;
    target: NonNullable<Scheduler>["targets"][number];
    schedule: NonNullable<Scheduler>["targets"][number]["scheduledTypes"][number];
  };
  const targetRows: ScheduledTargetRow[] = useMemo(() => {
    const list: ScheduledTargetRow[] = [];
    for (const tgt of scheduler?.targets ?? []) {
      for (const s of tgt.scheduledTypes) {
        list.push({
          workspaceId: tgt.workspaceId,
          environment: tgt.environment,
          serviceName: tgt.serviceName,
          type: s.type,
          lastEnqueuedAt: s.lastEnqueuedAt,
          lastStatus: s.lastStatus,
          target: tgt,
          schedule: s
        });
      }
    }
    return list;
  }, [scheduler]);
  const tTargets = useTableState(targetRows as unknown as Array<Record<string, unknown>>, {
    searchFields: ["workspaceId", "environment", "serviceName", "type", "lastStatus"],
    initialPageSize: 25
  });

  const counts = useMemo(() => {
    const c: Partial<Record<TabKey, number>> = {};
    for (const t of JOB_TABS) c[t.key] = jobs.filter(t.matcher).length;
    c.schedules = scheduler?.targets.length ?? 0;
    return c;
  }, [jobs, scheduler]);

  const onBulkRequeue = async () => {
    if (!confirm("Requeue ALL dead-lettered jobs?")) return;
    try {
      const r = await api.bulkRequeueDeadLettered();
      setBanner(`Requeued ${r.requeued} dead-lettered jobs (${r.failed} failed).`);
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };
  const onBulkDelete = async () => {
    if (!confirm("Permanently delete ALL dead-lettered jobs? This cannot be undone.")) return;
    try {
      const r = await api.bulkDeleteDeadLettered();
      setBanner(`Deleted ${r.deleted} dead-lettered jobs.`);
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };
  const onDeleteJob = async (jobId: string) => {
    if (!confirm(`Permanently delete dead-lettered job ${jobId}?`)) return;
    try {
      await api.bulkDeleteDeadLettered({ jobIds: [jobId] });
      setBanner(`Deleted ${jobId}.`);
      setJobs((prev) => prev.filter((row) => row.id !== jobId));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };
  const onTriggerSchedule = async (
    target: { workspaceId: string; environment: string },
    type:
      | "aggregate-metadata-sync"
      | "tracker-metadata-sync"
      | "submitted-aggregate-sync"
      | "tracker-events-sync"
      | "tally-recompute"
  ) => {
    try {
      await api.triggerScheduledSync({ ...target, type });
      setBanner(`Triggered ${type} for ${target.workspaceId}/${target.environment}.`);
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>
            Jobs &amp; dead-letter queue
            <span
              className={`pill pill-${live.connectionState === "open" ? "ok" : live.connectionState === "connecting" ? "info" : "muted"}`}
              style={{ fontSize: 11, marginLeft: 12, verticalAlign: "middle" }}
              title={`Live event stream: ${live.connectionState}. ${live.events.length} job event${live.events.length === 1 ? "" : "s"} since open.`}
            >
              {live.connectionState === "open"
                ? `● Live · ${live.events.length} events`
                : live.connectionState === "connecting" ? "Connecting…" : "Offline (polling)"}
            </span>
          </h1>
          <p>
            Sorted with running jobs at the top. Live status updates stream in via the
            platform event bus; a 15-second poll backstops missed events.
          </p>
        </div>
        <div className="actions">
          <button className="btn" onClick={() => void refresh()} disabled={loading}>Refresh</button>
        </div>
      </header>

      {error && <div className="alert alert-error">{error}</div>}
      {banner && <div className="alert alert-ok">{banner}</div>}

      <div className="tabs" style={{ display: "flex", gap: 4, flexWrap: "wrap", borderBottom: "1px solid #e5e7eb", marginBottom: 12 }}>
        {JOB_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`btn ${activeTab === t.key ? "btn-primary" : ""}`}
            style={{ borderRadius: "6px 6px 0 0" }}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label} <span className="pill pill-muted" style={{ marginLeft: 6 }}>{counts[t.key] ?? 0}</span>
          </button>
        ))}
        <button
          type="button"
          className={`btn ${activeTab === "schedules" ? "btn-primary" : ""}`}
          style={{ borderRadius: "6px 6px 0 0" }}
          onClick={() => setActiveTab("schedules")}
        >
          Schedules <span className="pill pill-muted" style={{ marginLeft: 6 }}>{counts.schedules ?? 0}</span>
        </button>
      </div>

      {activeTab !== "schedules" && (
        <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
            <span className="muted" style={{ fontSize: 12 }}>Type</span>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="all">All types ({types.length})</option>
              {types.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
            <span className="muted" style={{ fontSize: 12 }}>Workspace</span>
            <select value={workspaceFilter} onChange={(e) => setWorkspaceFilter(e.target.value)}>
              <option value="all">All workspaces ({workspaces.length})</option>
              {workspaces.map((w) => <option key={w} value={w}>{w}</option>)}
            </select>
          </label>
          {workspaceFilter !== "all" && (
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => setWorkspaceFilter("all")}
              title="Clear workspace filter"
            >
              ✕ Clear workspace
            </button>
          )}
          {activeTab === "deadlettered" && (counts.deadlettered ?? 0) > 0 && (
            <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
              <button type="button" className="btn btn-sm" onClick={() => void onBulkRequeue()}>Bulk requeue all</button>
              <button type="button" className="btn btn-sm btn-danger" onClick={() => void onBulkDelete()}>Bulk delete all</button>
            </span>
          )}
        </div>
      )}

      {activeTab === "schedules" ? (
        <SchedulesPanel
          scheduler={scheduler}
          onTrigger={onTriggerSchedule}
          loading={loading}
          targetRows={targetRows}
          tTargets={tTargets}
        />
      ) : loading && jobs.length === 0 ? (
        <SkeletonTable rows={5} columns={4} />
      ) : filtered.length === 0 ? (
        <div className="empty">
          <p>No jobs match the current filters.</p>
        </div>
      ) : (
        <div>
          <TableToolbar
            hideExport
            search={t.search}
            setSearch={t.setSearch}
            page={t.page}
            setPage={t.setPage}
            pageSize={t.pageSize}
            setPageSize={t.setPageSize}
            totalPages={t.totalPages}
            totalRows={t.totalRows}
            searchPlaceholder="Search id / type / workspace…"
          />
          <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Type</th>
                <th>Status</th>
                <th>Workspace</th>
                <th>Progress</th>
                <th>Requested</th>
                <th>Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(t.paged as unknown as ReplayJob[]).map((j) => {
                const ws = (j as { workspaceId?: string }).workspaceId ?? "—";
                const pct = typeof j.progress === "number" ? Math.round(j.progress) : null;
                const isRunning = j.status === "running" || j.status === "active";
                return (
                  <tr key={j.id} onClick={() => setSelected(j)} style={{ cursor: "pointer" }}>
                    <td><code>{j.id.slice(0, 12)}…</code></td>
                    <td>{j.type ?? "—"}</td>
                    <td>{jobPill(j.status)}</td>
                    <td className="soft">{ws}</td>
                    <td style={{ minWidth: 120 }}>
                      {isRunning ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <div style={{ flex: 1, height: 6, background: "#e5e7eb", borderRadius: 3, overflow: "hidden" }}>
                            <div
                              style={{
                                height: "100%",
                                width: pct != null && pct > 0 ? `${pct}%` : "30%",
                                background: "#2563eb",
                                animation: pct == null || pct === 0 ? "ts-pulse 1.4s ease-in-out infinite" : undefined,
                                transition: "width 200ms ease"
                              }}
                            />
                          </div>
                          <span style={{ fontSize: 11, minWidth: 32, textAlign: "right" }}>
                            {pct != null && pct > 0 ? `${pct}%` : "…"}
                          </span>
                        </div>
                      ) : pct != null ? (
                        `${pct}%`
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="soft">{fmt(j.createdAt ?? (j as { requestedAt?: string }).requestedAt)}</td>
                    <td className="soft">{fmt(j.updatedAt ?? (j as { completedAt?: string }).completedAt)}</td>
                    <td>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }} onClick={(e) => e.stopPropagation()}>
                        <button type="button" className="btn btn-sm" onClick={() => setSelected(j)}>
                          Details
                        </button>
                        {(j.status === "queued" || j.status === "running") && (
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={async () => {
                              try {
                                const { job } = await api.cancelJob(j.id);
                                setJobs((prev) => prev.map((row) => (row.id === j.id ? job : row)));
                              } catch (e) {
                                setError(e instanceof ApiError ? e.message : String(e));
                              }
                            }}
                          >
                            Cancel
                          </button>
                        )}
                        {(j.status === "failed" || j.status === "completed" || j.status === "succeeded") && (
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={async () => {
                              try {
                                const { job } = await api.retryJob(j.id);
                                setJobs((prev) => [job, ...prev]);
                              } catch (e) {
                                setError(e instanceof ApiError ? e.message : String(e));
                              }
                            }}
                          >
                            Retry
                          </button>
                        )}
                        {j.status === "dead-lettered" && (
                          <>
                            <button
                              type="button"
                              className="btn btn-sm"
                              onClick={async () => {
                                try {
                                  const { job } = await api.requeueJob(j.id);
                                  setJobs((prev) => [job, ...prev]);
                                } catch (e) {
                                  setError(e instanceof ApiError ? e.message : String(e));
                                }
                              }}
                            >
                              Requeue
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={() => void onDeleteJob(j.id)}
                            >
                              Delete
                            </button>
                          </>
                        )}
                        {ws !== "—" && (
                          <Link className="btn btn-sm" to={`/admin/workspaces/${encodeURIComponent(ws)}`}>
                            Workspace
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {selected && <JobDetailDrawer job={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

type ScheduledTargetRowExternal = {
  workspaceId: string;
  environment: string;
  serviceName: string;
  type: string;
  lastEnqueuedAt?: string | null;
  lastStatus?: string | null;
  target: NonNullable<Scheduler>["targets"][number];
  schedule: NonNullable<Scheduler>["targets"][number]["scheduledTypes"][number];
};

function SchedulesPanel({
  scheduler,
  onTrigger,
  loading,
  targetRows,
  tTargets
}: {
  scheduler: Scheduler | null;
  onTrigger: (
    target: { workspaceId: string; environment: string },
    type:
      | "aggregate-metadata-sync"
      | "tracker-metadata-sync"
      | "submitted-aggregate-sync"
      | "tracker-events-sync"
      | "tally-recompute"
  ) => Promise<void>;
  loading: boolean;
  targetRows: ScheduledTargetRowExternal[];
  tTargets: ReturnType<typeof useTableState>;
}) {
  if (loading && !scheduler) return <SkeletonTable rows={5} columns={4} />;
  if (!scheduler) {
    return (
      <div className="empty">
        <p>The scheduled-sync inspection endpoint isn't reachable. The worker may not have written its first heartbeat yet.</p>
      </div>
    );
  }
  const beat = scheduler.scheduler;
  return (
    <>
      <section className="card" style={{ marginBottom: 12 }}>
        <h2>Scheduler heartbeat</h2>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {beat.lastTickAt ? (
            <span className={`pill ${beat.stale ? "pill-warn" : "pill-info"}`}>
              Last tick: {new Date(beat.lastTickAt).toLocaleString()}
            </span>
          ) : (
            <span className="pill pill-muted">No heartbeat yet</span>
          )}
          {beat.intervalMs != null && <span className="pill pill-muted">Interval: {Math.round(beat.intervalMs / 1000)}s</span>}
          {beat.nextTickAt && <span className="pill pill-info">Next tick ~ {new Date(beat.nextTickAt).toLocaleString()}</span>}
          {beat.lastEnqueued != null && <span className="pill pill-muted">Last enqueued: {beat.lastEnqueued}</span>}
          {beat.lastSkippedReason && <span className="pill pill-warn">Skipped: {beat.lastSkippedReason}</span>}
          {beat.lastError && <span className="pill pill-err">Error: {beat.lastError}</span>}
        </div>
        <p className="muted" style={{ marginTop: 8, fontSize: 12 }}>
          Includes — metadata: {String(beat.includeMetadata ?? "default")} · aggregate: {String(beat.includeAggregateFacts ?? "default")} · tracker: {String(beat.includeTracker ?? "default")}
        </p>
      </section>

      <section className="card">
        <h2>Scheduled targets</h2>
        {targetRows.length === 0 ? (
          <div className="empty">
            <p>No enabled service profiles. Add one in Platform settings to start scheduled syncs.</p>
          </div>
        ) : (
          <div>
            <TableToolbar
              search={tTargets.search} setSearch={tTargets.setSearch}
              page={tTargets.page} setPage={tTargets.setPage}
              pageSize={tTargets.pageSize} setPageSize={tTargets.setPageSize}
              totalPages={tTargets.totalPages} totalRows={tTargets.totalRows}
              hideExport
              searchPlaceholder="Search workspace / environment / service…"
            />
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Workspace</th>
                    <th>Environment</th>
                    <th>Service</th>
                    <th>Job type</th>
                    <th>Last enqueued</th>
                    <th>Last status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(tTargets.paged as unknown as ScheduledTargetRowExternal[]).map((row) => (
                    <tr key={`${row.workspaceId}-${row.environment}-${row.type}`}>
                      <td><code>{row.workspaceId}</code></td>
                      <td>{row.environment}</td>
                      <td className="soft">{row.serviceName}</td>
                      <td>{row.type}</td>
                      <td className="soft">{row.lastEnqueuedAt ? new Date(row.lastEnqueuedAt).toLocaleString() : "—"}</td>
                      <td>{row.lastStatus ? jobPill(row.lastStatus) : <span className="soft">—</span>}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => void onTrigger({ workspaceId: row.workspaceId, environment: row.environment }, row.schedule.type)}
                        >
                          Trigger now
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </>
  );
}

function JobDetailDrawer({ job, onClose }: { job: ReplayJob; onClose: () => void }) {
  const [detail, setDetail] = useState<ReplayJob | null>(null);
  const [detailErr, setDetailErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.request<{ job: ReplayJob }>(`/api/jobs/${encodeURIComponent(job.id)}`)
      .then((d) => { if (!cancelled) setDetail(d.job); })
      .catch((e: unknown) => { if (!cancelled) setDetailErr(e instanceof Error ? e.message : String(e)); });
    return () => { cancelled = true; };
  }, [job.id]);

  const view: ReplayJob = detail ?? job;
  const input = (view as { input?: unknown }).input;
  const result = (view as { result?: unknown }).result;
  const err = (view as { error?: unknown }).error;
  const ws = (view as { workspaceId?: string }).workspaceId;

  return (
    <Drawer
      open={true}
      title={`Job · ${view.id}`}
      subtitle={view.type ?? undefined}
      onClose={onClose}
      width={620}
      footer={<button type="button" className="btn" onClick={onClose}>Close</button>}
    >
      {detailErr && <div className="alert alert-warn">Couldn't load detail: {detailErr}</div>}
      <dl className="dl-kv">
        <dt>Type</dt><dd>{view.type ?? "—"}</dd>
        <dt>Status</dt><dd>{jobPill(view.status)}</dd>
        <dt>Workspace</dt><dd>{ws ?? "—"}</dd>
        <dt>Progress</dt><dd>{typeof view.progress === "number" ? `${Math.round(view.progress)}%` : "—"}</dd>
        <dt>Requested</dt><dd>{fmt(view.createdAt ?? (view as { requestedAt?: string }).requestedAt)}</dd>
        <dt>Updated</dt><dd>{fmt(view.updatedAt ?? (view as { completedAt?: string }).completedAt)}</dd>
      </dl>
      {err != null && (
        <>
          <h3 style={{ marginTop: 16 }}>Error</h3>
          <pre style={{ whiteSpace: "pre-wrap", background: "#fef2f2", padding: 8, borderRadius: 4 }}>
            {typeof err === "string" ? err : JSON.stringify(err, null, 2)}
          </pre>
        </>
      )}
      {input != null && (
        <>
          <h3 style={{ marginTop: 16 }}>Input</h3>
          <pre style={{ whiteSpace: "pre-wrap", background: "var(--surface-alt)", padding: 8, borderRadius: 4 }}>
            {JSON.stringify(input, null, 2)}
          </pre>
        </>
      )}
      {result != null && (
        <>
          <h3 style={{ marginTop: 16 }}>Result</h3>
          <pre style={{ whiteSpace: "pre-wrap", background: "var(--surface-alt)", padding: 8, borderRadius: 4 }}>
            {JSON.stringify(result, null, 2)}
          </pre>
        </>
      )}
    </Drawer>
  );
}
